import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { get, post } from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';
import { createEchoInstance } from '../lib/echo';

export const MENU_ITEMS = [
  { key: 'dashboard',               label: 'Dashboard',        group: 'Main Navigation' },
  { key: 'leads',                   label: 'Leads',            group: 'Main Navigation' },
  { key: 'workflows',               label: 'Workflow Builder', group: 'Main Navigation' },
  { key: 'campaigns',               label: 'Campaigns',        group: 'Main Navigation' },
  { key: 'sequences',               label: 'Sequences',        group: 'Main Navigation' },
  { key: 'replies',                 label: 'Replies',          group: 'Main Navigation' },
  { key: 'analytics',               label: 'Analytics',        group: 'Main Navigation' },
  { key: 'settings_profile',        label: 'Profile',          group: 'Settings' },
  { key: 'settings_account',        label: 'Account',          group: 'Settings' },
  { key: 'settings_billing',        label: 'Billing',          group: 'Settings' },
  { key: 'settings_email_setup',    label: 'Email Setup',      group: 'Settings' },
  { key: 'settings_team',           label: 'Team',             group: 'Settings' },
  { key: 'settings_menu_permissions', label: 'Menu Permissions', group: 'Settings' },
  { key: 'settings_notifications',  label: 'Notifications',    group: 'Settings' },
] as const;

export type MenuKey = typeof MENU_ITEMS[number]['key'];

export interface MemberMenuPermission {
  user_id: string;
  allowed_menus: MenuKey[];
}

interface MenuPermissionsData {
  permissions: MemberMenuPermission[];
}

interface MyMenuPermissionsData {
  allowed_menus: MenuKey[];
}

/** Fetches the allowed menus for the currently logged-in user. */
export function useMyMenuPermissions() {
  const { user } = useAuth();
  const isMember = user?.orgRole === 'member' || user?.orgRole === 'admin';
  return useQuery({
    queryKey: ['menuPermissions', 'me'],
    queryFn: () => get<MyMenuPermissionsData>('settings/menu-permissions/me'),
    enabled: !!user,
    // Poll every 60 s as fallback when Pusher is not configured
    refetchInterval: isMember ? 60_000 : false,
    refetchOnWindowFocus: isMember,
  });
}

/**
 * Subscribe to Pusher for instant menu permission updates.
 * Falls back to the polling in useMyMenuPermissions when Pusher is not configured.
 * Mount this once in the Sidebar so all sessions see changes immediately.
 */
export function useMenuPermissionsRealtime(): void {
  const { user, token } = useAuth();
  const queryClient = useQueryClient();
  const orgId = user?.organization?.id ?? null;
  const currentUserId = user?.id ?? null;

  useEffect(() => {
    if (!token || !orgId) return;

    try {
      const echo = createEchoInstance(token);
      if (!echo) return; // Pusher not configured — polling handles this

      const channelName = `org.${orgId}`;

      echo.private(channelName).listen('.MenuPermissionsUpdated', (e: { user_id: string }) => {
        if (e.user_id === currentUserId) {
          queryClient.invalidateQueries({ queryKey: ['menuPermissions', 'me'] });
        }
      });

      return () => {
        echo.leave(channelName);
      };
    } catch (err) {
      console.warn('[useMenuPermissionsRealtime] Pusher init failed:', err);
    }
  }, [token, orgId, currentUserId, queryClient]);
}

export function useMenuPermissionsQuery(enabled = true) {
  return useQuery({
    queryKey: ['menuPermissions'],
    queryFn: () => get<MenuPermissionsData>('settings/menu-permissions'),
    enabled,
  });
}

export function useUpdateMenuPermissionsMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: MemberMenuPermission) =>
      post(`settings/menu-permissions/${data.user_id}`, { allowed_menus: data.allowed_menus }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menuPermissions'] });
      queryClient.invalidateQueries({ queryKey: ['menuPermissions', 'me'] });
    },
  });
}
